import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'md' | 'lg';

interface StyleProps {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
}

const variantStyles = {
  primary: css`
    background: var(--accent);
    color: var(--on-accent);
    border: 1px solid var(--accent);

    &:hover {
      background: var(--accent-hover);
      border-color: var(--accent-hover);
    }

    &:active {
      background: var(--accent-pressed);
      border-color: var(--accent-pressed);
    }
  `,
  secondary: css`
    background: transparent;
    color: var(--text-1);
    border: 1px solid var(--border-strong);

    &:hover {
      background: var(--surface-hover);
      border-color: rgba(255, 255, 255, 0.28);
    }
  `,
  ghost: css`
    background: transparent;
    color: var(--text-1);
    border: 1px solid transparent;
    padding-left: 0.25rem;
    padding-right: 0.25rem;

    &:hover {
      color: var(--accent);
    }
  `,
};

export const buttonStyles = css<StyleProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: ${({ $size }) => ($size === 'lg' ? '52px' : '48px')};
  padding: 0 ${({ $size }) => ($size === 'lg' ? '1.5rem' : '1.15rem')};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  border-radius: var(--radius-sm);
  font-family: var(--font-body);
  font-size: ${({ $size }) => ($size === 'lg' ? '1rem' : '0.9375rem')};
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.005em;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease, transform 0.18s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 3px;
  }

  &:disabled,
  &[aria-disabled='true'] {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    flex-shrink: 0;
  }

  ${({ $variant }) => variantStyles[$variant]}
`;

const StyledButton = styled.button<StyleProps>`
  ${buttonStyles}
`;

const StyledLink = styled(Link)<StyleProps>`
  ${buttonStyles}
`;

const StyledAnchor = styled.a<StyleProps>`
  ${buttonStyles}
`;

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
  children: React.ReactNode;
}

type AsButton = CommonProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps> & { to?: never; href?: never };
type AsLink = CommonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonProps | 'href'> & { to: string; href?: never };
type AsAnchor = CommonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonProps> & { href: string; to?: never };

export type ButtonProps = AsButton | AsLink | AsAnchor;

const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>((props, ref) => {
  const { variant = 'primary', size = 'md', fullWidth = false, children, ...rest } = props;
  const styleProps = { $variant: variant, $size: size, $fullWidth: fullWidth };

  if ('to' in rest && rest.to !== undefined) {
    const { to, ...linkRest } = rest as AsLink;
    return (
      <StyledLink ref={ref as React.Ref<HTMLAnchorElement>} to={to} {...styleProps} {...linkRest}>
        {children}
      </StyledLink>
    );
  }

  if ('href' in rest && rest.href !== undefined) {
    const anchorRest = rest as AsAnchor;
    const isExternal = /^https?:\/\//.test(anchorRest.href);
    return (
      <StyledAnchor
        ref={ref as React.Ref<HTMLAnchorElement>}
        {...styleProps}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...anchorRest}
      >
        {children}
      </StyledAnchor>
    );
  }

  const buttonRest = rest as AsButton;
  return (
    <StyledButton ref={ref as React.Ref<HTMLButtonElement>} type="button" {...styleProps} {...buttonRest}>
      {children}
    </StyledButton>
  );
});

Button.displayName = 'Button';

export default Button;
